import { api, getJson, sendJson } from '@/lib/api';
import type { PillTone } from '@bcecloud/portal-ui';
import { formatShortDate } from '@/proposals/lib/money';

/**
 * Client types + calls for Tools → Invoices (api/v3/invoices). A dropped
 * vendor invoice is read in the background into its header and lines; the
 * list polls while any invoice is still being read.
 */

export const INVOICES_PATH = '/invoices';

export function invoicePath(id: string): string {
  return `${INVOICES_PATH}/${encodeURIComponent(id)}`;
}

export const INVOICE_STATUS = {
  parsing: 'parsing',
  needsReview: 'needs_review',
  partlyPosted: 'partly_posted',
  posted: 'posted',
  failed: 'failed',
} as const;
export type InvoiceStatus = (typeof INVOICE_STATUS)[keyof typeof INVOICE_STATUS];

export const INVOICE_STATUS_LABELS: Readonly<Record<InvoiceStatus, string>> = {
  [INVOICE_STATUS.parsing]: 'Reading',
  [INVOICE_STATUS.needsReview]: 'Needs review',
  [INVOICE_STATUS.partlyPosted]: 'Partly posted',
  [INVOICE_STATUS.posted]: 'Posted',
  [INVOICE_STATUS.failed]: "Couldn't read",
};

export const INVOICE_STATUS_TONES: Readonly<Record<InvoiceStatus, PillTone>> = {
  [INVOICE_STATUS.parsing]: 'info',
  [INVOICE_STATUS.needsReview]: 'warning',
  [INVOICE_STATUS.partlyPosted]: 'brand',
  [INVOICE_STATUS.posted]: 'success',
  [INVOICE_STATUS.failed]: 'danger',
};

/** The file types the drop zone accepts (the server enforces the same). */
export const INVOICE_ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp';

/** How often the list and detail re-check an invoice that is still being read. */
export const READING_POLL_MS = 3000;

export interface InvoiceDocument {
  readonly id: string;
  readonly fileName: string;
  readonly fileType: string;
  readonly status: InvoiceStatus;
  readonly error: string | null;
  readonly vendorName: string | null;
  readonly vendorEmail: string | null;
  readonly invoiceNumber: string | null;
  readonly invoiceDate: string | null;
  readonly dueDate: string | null;
  readonly periodStart: string | null;
  readonly periodEnd: string | null;
  readonly total: number | null;
  readonly vendorAccountId: string | null;
  readonly vendorAccountName: string | null;
  readonly filesAttachedTo: readonly string[];
  readonly unassignedContractNumbers: readonly string[];
  readonly qboBillIds: readonly string[];
  readonly qboError: string | null;
  readonly lineCount: number;
  readonly totalsGap: number | null;
  readonly source: string | null;
  readonly duplicateOfId: string | null;
  readonly createdAt: string;
  readonly parsedAt: string | null;
}

/** How sure a line's contract match is (mirrors lib/invoices/invoice-matching.ts). */
export const CONTRACT_MATCH_STATUS = {
  matched: 'matched',
  suggested: 'suggested',
  ambiguous: 'ambiguous',
  unmatched: 'unmatched',
} as const;
export type ContractMatchStatus = (typeof CONTRACT_MATCH_STATUS)[keyof typeof CONTRACT_MATCH_STATUS];

export const CONTRACT_MATCH_REASON = {
  contractNumber: 'contract_number',
  contractFamily: 'contract_family',
  dateAndPlace: 'date_and_place',
  dateOnly: 'date_only',
  contractNotFound: 'contract_not_found',
  none: 'none',
} as const;
export type ContractMatchReason = (typeof CONTRACT_MATCH_REASON)[keyof typeof CONTRACT_MATCH_REASON];

export const CONTRACT_MATCH_LABELS: Readonly<Record<ContractMatchStatus, string>> = {
  [CONTRACT_MATCH_STATUS.matched]: 'Matched',
  [CONTRACT_MATCH_STATUS.suggested]: 'Check',
  [CONTRACT_MATCH_STATUS.ambiguous]: 'Pick one',
  [CONTRACT_MATCH_STATUS.unmatched]: 'No match',
};

export const CONTRACT_MATCH_TONES: Readonly<Record<ContractMatchStatus, PillTone>> = {
  [CONTRACT_MATCH_STATUS.matched]: 'success',
  [CONTRACT_MATCH_STATUS.suggested]: 'info',
  [CONTRACT_MATCH_STATUS.ambiguous]: 'warning',
  [CONTRACT_MATCH_STATUS.unmatched]: 'neutral',
};

/** Why a match was made, in the reviewer's words. */
export const CONTRACT_MATCH_REASON_TEXT: Readonly<Record<ContractMatchReason, string>> = {
  [CONTRACT_MATCH_REASON.contractNumber]: 'Contract number on the invoice',
  [CONTRACT_MATCH_REASON.contractFamily]: 'Same contract family as the number on the invoice',
  [CONTRACT_MATCH_REASON.dateAndPlace]: 'Event date and the client or city on the line',
  [CONTRACT_MATCH_REASON.dateOnly]: 'Events around the date on the line',
  [CONTRACT_MATCH_REASON.contractNotFound]: "The contract number on the invoice isn't in BCE Cloud",
  [CONTRACT_MATCH_REASON.none]: 'Nothing on the line pointed to an event',
};

export interface CandidateEvent {
  readonly eventId: string;
  readonly opportunityId: string;
  readonly contractNumber: string | null;
  readonly name: string | null;
  readonly accountName: string | null;
  readonly city: string | null;
  readonly programName: string | null;
  readonly eventDate: string | null;
}

export interface ContractMatch {
  readonly status: ContractMatchStatus;
  readonly reason: ContractMatchReason;
  readonly event: CandidateEvent | null;
  readonly candidates: readonly CandidateEvent[];
}

export interface InvoiceLine {
  readonly id: string;
  readonly position: number;
  readonly description: string;
  readonly serviceDate: string | null;
  readonly quantity: number | null;
  readonly unitPrice: number | null;
  readonly amount: number;
  readonly contractNumber: string | null;
  readonly eventReference: string | null;
  readonly contractMatch: ContractMatch | null;
  readonly categoryId: string | null;
  readonly category: string | null;
  readonly assignment: CandidateEvent | null;
  readonly assignmentSource: ChoiceSource | null;
  readonly categorySource: ChoiceSource | null;
  readonly parentLineId: string | null;
  readonly holdReason: string | null;
  readonly expenseId: string | null;
  readonly statementId: string | null;
  readonly postError: string | null;
  readonly qboBillId: string | null;
  readonly status: InvoiceLineStatus;
}

export const LINE_STATUS = {
  needsReview: 'needs_review',
  ready: 'ready',
  excluded: 'excluded',
  quarantined: 'quarantined',
  posting: 'posting',
  posted: 'posted',
  failed: 'failed',
  split: 'split',
} as const;
export type InvoiceLineStatus = (typeof LINE_STATUS)[keyof typeof LINE_STATUS];

export const LINE_STATUS_LABELS: Readonly<Record<InvoiceLineStatus, string>> = {
  [LINE_STATUS.needsReview]: 'Needs review',
  [LINE_STATUS.ready]: 'Ready',
  [LINE_STATUS.excluded]: 'Excluded',
  [LINE_STATUS.quarantined]: 'Held',
  [LINE_STATUS.posting]: 'Posting',
  [LINE_STATUS.posted]: 'Posted',
  [LINE_STATUS.failed]: 'Failed',
  [LINE_STATUS.split]: 'Split',
};

export const LINE_STATUS_TONES: Readonly<Record<InvoiceLineStatus, PillTone>> = {
  [LINE_STATUS.needsReview]: 'warning',
  [LINE_STATUS.ready]: 'success',
  [LINE_STATUS.excluded]: 'neutral',
  [LINE_STATUS.quarantined]: 'info',
  [LINE_STATUS.posting]: 'info',
  [LINE_STATUS.posted]: 'brand',
  [LINE_STATUS.failed]: 'danger',
  [LINE_STATUS.split]: 'neutral',
};

/** Statuses a reviewer can still change. */
export const OPEN_LINE_STATUSES: readonly InvoiceLineStatus[] = [LINE_STATUS.needsReview, LINE_STATUS.ready];
export const HELD_LINE_STATUSES: readonly InvoiceLineStatus[] = [LINE_STATUS.excluded, LINE_STATUS.quarantined];

export const CHOICE_SOURCE = { suggested: 'suggested', chosen: 'chosen' } as const;
export type ChoiceSource = (typeof CHOICE_SOURCE)[keyof typeof CHOICE_SOURCE];

export const HOLD_ACTION = { exclude: 'exclude', quarantine: 'quarantine' } as const;
export type HoldAction = (typeof HOLD_ACTION)[keyof typeof HOLD_ACTION];

export interface ExpenseCategory {
  readonly id: string;
  readonly name: string;
}

export interface VendorOption {
  readonly accountId: string;
  readonly name: string;
}

export interface LineEdit {
  readonly eventId?: string | null;
  readonly categoryId?: string | null;
}

export interface SplitPart {
  readonly amount: number;
  readonly description?: string;
}

export interface HeldLine extends InvoiceLine {
  readonly documentId: string;
  readonly vendorName: string | null;
  readonly invoiceNumber: string | null;
}

/** "3", or "3b" for the second part of a split line 3. */
export function lineLabel(line: Pick<InvoiceLine, 'position' | 'parentLineId'>): string {
  const whole = Math.floor(line.position);
  if (!line.parentLineId) return String(whole);
  const part = Math.round((line.position - whole) * 100);
  return `${whole}${String.fromCharCode(96 + part)}`;
}

export interface InvoiceDetail {
  readonly document: InvoiceDocument;
  readonly lines: readonly InvoiceLine[];
  /** Opportunity id → its Income & Expense Statement id, for the Salesforce links. */
  readonly statements: Readonly<Record<string, string>>;
}

export function listInvoices(signal?: AbortSignal): Promise<InvoiceDocument[]> {
  return getJson<InvoiceDocument[]>('/invoices', undefined, signal);
}

export function readInvoice(id: string, signal?: AbortSignal): Promise<InvoiceDetail> {
  return getJson<InvoiceDetail>(`/invoices/${encodeURIComponent(id)}`, undefined, signal);
}

export async function uploadInvoice(file: File): Promise<InvoiceDocument> {
  const form = new FormData();
  form.append('file', file);
  const response = await api.post<{ data: InvoiceDocument }>('/invoices', form);
  return response.data.data;
}

export function listExpenseCategories(signal?: AbortSignal): Promise<ExpenseCategory[]> {
  return getJson<ExpenseCategory[]>('/invoices/categories', undefined, signal);
}

export function searchContracts(q: string, signal?: AbortSignal): Promise<CandidateEvent[]> {
  return getJson<CandidateEvent[]>('/invoices/contract-options', { q }, signal);
}

export function searchVendors(q: string, signal?: AbortSignal): Promise<VendorOption[]> {
  return getJson<VendorOption[]>('/invoices/vendor-options', { q }, signal);
}

export function listHeldLines(signal?: AbortSignal): Promise<HeldLine[]> {
  return getJson<HeldLine[]>('/invoices/held-lines', undefined, signal);
}

export function editInvoiceLine(lineId: string, edit: LineEdit): Promise<InvoiceLine> {
  return sendJson<InvoiceLine>('patch', `/invoice-lines/${encodeURIComponent(lineId)}`, edit);
}

export function splitInvoiceLine(lineId: string, parts: readonly SplitPart[]): Promise<InvoiceLine[]> {
  return sendJson<InvoiceLine[]>('post', `/invoice-lines/${encodeURIComponent(lineId)}/split`, { parts });
}

export function holdInvoiceLine(lineId: string, action: HoldAction, reason: string): Promise<InvoiceLine> {
  return sendJson<InvoiceLine>('post', `/invoice-lines/${encodeURIComponent(lineId)}/${action}`, reason ? { reason } : {});
}

export function releaseInvoiceLine(lineId: string): Promise<InvoiceLine> {
  return sendJson<InvoiceLine>('post', `/invoice-lines/${encodeURIComponent(lineId)}/release`);
}

export interface ApproveResult {
  readonly posted: number;
  readonly failed: ReadonlyArray<{ readonly lineId: string; readonly error: string }>;
  readonly filesAttached: number;
  readonly qbo: { readonly billId: string } | { readonly error: string } | null;
}

export function approveInvoice(id: string, options: { readonly createQboBill: boolean }): Promise<ApproveResult> {
  return sendJson<ApproveResult>('post', `/invoices/${encodeURIComponent(id)}/approve`, options);
}

/** Lines that will post, per contract: what the approve dialog lists. */
export interface PostingGroup {
  readonly opportunityId: string;
  readonly contractNumber: string | null;
  readonly label: string;
  readonly count: number;
  readonly total: number;
}

export function postingGroups(lines: readonly InvoiceLine[]): PostingGroup[] {
  const groups = new Map<string, PostingGroup>();
  for (const line of lines) {
    if (line.status !== LINE_STATUS.ready || !line.assignment) continue;
    const key = line.assignment.opportunityId;
    const current = groups.get(key);
    groups.set(key, {
      opportunityId: key,
      contractNumber: line.assignment.contractNumber,
      label: describeEvent(line.assignment),
      count: (current?.count ?? 0) + 1,
      total: Math.round(((current?.total ?? 0) + line.amount) * 100) / 100,
    });
  }
  return [...groups.values()];
}

export function setInvoiceVendor(id: string, vendor: VendorOption | null): Promise<InvoiceDocument> {
  return sendJson<InvoiceDocument>('put', `/invoices/${encodeURIComponent(id)}/vendor`, { vendor });
}

/** Parts of an even split, cents-exact (mirrors the API's evenSplit). */
export function evenSplit(amount: number, parts: number): number[] {
  const cents = Math.round(amount * 100);
  const base = Math.trunc(cents / parts);
  const remainder = cents - base * parts;
  return Array.from({ length: parts }, (_, index) => (base + (index < Math.abs(remainder) ? Math.sign(remainder) : 0)) / 100);
}

export function matchInvoiceAgain(id: string): Promise<InvoiceDetail> {
  return sendJson<InvoiceDetail>('post', `/invoices/${encodeURIComponent(id)}/match`);
}

/** "Acme Corp · Tampa · Sep 24, 2026": what tells a reviewer which event this is. */
export function describeEvent(event: Pick<CandidateEvent, 'accountName' | 'city' | 'eventDate'>): string {
  return [event.accountName, event.city, event.eventDate ? formatShortDate(event.eventDate) : null].filter(Boolean).join(' · ');
}

export function readInvoiceAgain(id: string): Promise<InvoiceDocument> {
  return sendJson<InvoiceDocument>('post', `/invoices/${encodeURIComponent(id)}/read-again`);
}

/** The original file's bytes (the route needs the staff token, so no plain link). */
export async function fetchInvoiceFile(id: string, signal?: AbortSignal): Promise<Blob> {
  const response = await api.get<Blob>(`/invoices/${encodeURIComponent(id)}/file`, {
    responseType: 'blob',
    ...(signal ? { signal } : {}),
  });
  return response.data;
}

/** Whether a list or document still has something being read. */
export function isReading(documents: readonly Pick<InvoiceDocument, 'status'>[]): boolean {
  return documents.some((document) => document.status === INVOICE_STATUS.parsing);
}
